import { isPast, isToday } from "date-fns";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import { Seo } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { NoScheduleToday } from "@/features/pre-call/components/no-schedule-today";
import { SessionPreview } from "@/features/pre-call/components/session-preview";
import { useGetSessionByKey } from "@/features/session/api/get-session-by-key";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { PreviewDevice } from "@/features/vidyo-devices/components/preview-device";
import { isRecurringSessionConflict } from "@/types/error";

export function PreCall() {
  const { key } = useParams();

  const {
    isError: isSessionError,
    data: session,
    isSuccess: isSessionSuccess,
    error,
  } = useGetSessionByKey(key ?? "");
  const setSession = useVidyoStore((state) => state.setSession);

  useEffect(() => {
    if (isSessionSuccess) {
      setSession({
        id: session.id,
        parentSession: session.parentSession,
      });
    }
  }, [isSessionSuccess, session?.id, session?.parentSession, setSession]);

  const isPastSession =
    isSessionSuccess && !isToday(session.end) && isPast(session.end);

  const userCannotJoinSession = isSessionSuccess && !session.canJoin;

  if (isSessionError) {
    if (isRecurringSessionConflict(error)) {
      return <NoScheduleToday recurringSessions={error.recurringSessions} />;
    }

    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Seo title="Session" />

        <div className="max-w-screen-sm px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-2 text-center text-2xl text-foreground">
            The meeting code that you entered doesn't work
          </p>
          <p className="mb-4 text-center text-sm text-muted">
            Make sure that you've entered the correct meeting code in the URL,
            e.g. {import.meta.env.VITE_APP_URL}/r/xxxxxxxxxxx
          </p>

          <Button size="lg" variant="outline" asChild>
            <Link to="/">Return to dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (isPastSession) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Seo title="Session" />

        <div className="max-w-screen-sm px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-6 text-center text-2xl text-foreground">
            This session has already ended
          </p>

          <Button size="lg" variant="outline" asChild>
            <Link to="/">Return to dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (userCannotJoinSession) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Seo title="Session" />

        <div className="max-w-screen-sm px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-6 text-center text-2xl text-foreground">
            You are not invited to this session, so you cannot join the call.
          </p>

          <Button size="lg" variant="outline" asChild>
            <Link to="/">Return to dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-xl px-4 pb-6 sm:px-6 md:h-full lg:px-8">
      <Seo title={isSessionSuccess ? session?.name : "Session"} />
      <div className="flex flex-col justify-center md:h-full">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
          <div className="col-span-7 flex items-center">
            <PreviewDevice />
          </div>
          <div className="col-span-5 flex items-center">
            <SessionPreview />
          </div>
        </div>
      </div>
    </div>
  );
}
